import { useLocale } from "./useLocale";
import type { Locale } from "./registry";

/**
 * Механизм локализации копий — один на оба сайта (BTQ-47, шаг A2 карты v2).
 *
 * Правило карты v2: португальская страница — это английская страница на другом
 * языке. Тот же компонент, та же вёрстка, та же композиция. Значит текст обязан
 * уехать из разметки в контент-слой, а компонент — брать его по локали.
 *
 * Эталон, с которого снят механизм: src/pages/Contact.tsx + content/pages/contact.ts.
 * Отличие здесь одно и важное: там обе локали заполнены целиком, а в Фазе A
 * португальских строк ещё НЕТ ВОВСЕ — их пишет Фаза B. Поэтому:
 *
 *   PT-строки нет → рендерится английская, и элемент помечается
 *   data-i18n-fallback="en". Страница существует и выглядит правильно,
 *   просто читается по-английски.
 *
 * 🔴 Английский текст под /pt/ не попадает на прод ни при каких обстоятельствах:
 * пары остаются в состоянии planned, их адреса отдают 404, а клон живёт и
 * проверяется только на preview. Фолбэк — инструмент проверки структуры, а не
 * способ выкатить полусайт.
 *
 * ── Байт-идентичность английской стороны ──
 *
 * Условие приёмки Фазы A — английский вывод не меняется побайтово. Механизм
 * держит это конструктивно: на английской локали resolveCopy возвращает объект
 * `en` КАК ЕСТЬ (без копирования и слияния), а `fb` равен undefined. Спред
 * `{...undefined}` не добавляет в разметку ничего, поэтому атрибут появляется
 * только на португальской стороне.
 *
 * Второй источник расхождений — форма JSX, а не этот файл: подстановка вместо
 * литерала безопасна, только если литерал был ЕДИНСТВЕННЫМ ребёнком элемента.
 * Иначе React вставляет между текстовыми узлами разделитель <!-- -->. Ловушка
 * стреляла трижды (d0d1c90, 55a70c0, 1e9fdaf в lumen-studio-prod). Проверка —
 * btq.pt/tools/mixed-children.mjs, гонять перед каждой правкой компонента.
 */

/**
 * Копия одного блока страницы на двух языках.
 *
 * `pt` необязателен и может быть частичным: в Фазе B волны приходят по одной,
 * и половина переведённого блока не должна ронять другую половину.
 */
export interface LocaleCopy<T> {
  en: T;
  pt?: DeepPartial<T> | null;
}

export type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends readonly unknown[]
    ? T[K]
    : T[K] extends object
      ? DeepPartial<T[K]>
      : T[K];
};

export interface Resolved<T> {
  /** Текст для рендера: португальский там, где он есть, иначе английский. */
  t: T;
  /** Хоть одна строка блока осталась английской. */
  fellBack: boolean;
  /**
   * Готовый спред для корня блока: `{...fb}`.
   *
   * undefined на английской стороне — именно поэтому английская разметка не
   * меняется. На португальской — data-i18n-fallback="en", по которому
   * непереведённое видно и глазом в инспекторе, и грепом по собранному HTML.
   */
  fb: { "data-i18n-fallback": "en" } | undefined;
}

const FALLBACK_ATTR = { "data-i18n-fallback": "en" } as const;

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/**
 * Накладывает переведённые строки на английские.
 *
 * Массивы заменяются целиком, а не поэлементно: список услуг или карточек
 * переводится как список, и слияние по индексу дало бы франкенштейна из
 * половин, если длина разошлась.
 *
 * Пустая строка считается ОТСУТСТВИЕМ перевода, а не переводом в пустоту:
 * пустой заголовок на странице — это дефект, а не решение.
 */
function merge<T>(en: T, pt: unknown, missing: { any: boolean }): T {
  if (pt === undefined || pt === null || pt === "") {
    missing.any = true;
    return en;
  }
  if (isPlainObject(en) && isPlainObject(pt)) {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(en)) {
      out[key] = merge((en as Record<string, unknown>)[key], pt[key], missing);
    }
    return out as T;
  }
  return pt as T;
}

export function resolveCopy<T>(copy: LocaleCopy<T>, locale: Locale): Resolved<T> {
  // Английская сторона не проходит через merge вообще — ни копии объекта, ни
  // нового массива. Так исключается любой шанс сдвинуть английский вывод.
  if (locale === "en") return { t: copy.en, fellBack: false, fb: undefined };

  const missing = { any: false };
  const t = merge(copy.en, copy.pt, missing);
  return { t, fellBack: missing.any, fb: missing.any ? FALLBACK_ATTR : undefined };
}

/** То же самое внутри компонента: локаль берётся из текущего пути. */
export function useCopy<T>(copy: LocaleCopy<T>): Resolved<T> {
  return resolveCopy(copy, useLocale());
}
